import { create } from 'zustand'
import type { User } from 'firebase/auth'
import {
  collection, deleteDoc, doc, getDocs, onSnapshot, orderBy, query, setDoc, updateDoc,
  writeBatch,
} from 'firebase/firestore'
import { db } from './firebase'
import {
  DAYS_IN_TRASH, type Device, type Doc, type Folder, type Note, type Scope, type SortKey,
  type Theme, type Todo, type TodoStatus, type UndoEntry, type Version, type ViewMode,
} from './types'
import { DEVICE_ID, describeDevice } from './lib/device'
import { signOut } from './firebase'
import { EMPTY_DOC, deriveMeta, matchesQuery } from './lib/format'
import { prunable, sameDoc, shouldSnapshot } from './lib/history'
import { backupDue, buildSnapshot, fingerprint } from './lib/backup'
import { writeBackup } from './lib/backupStore'

interface State {
  user: User | null
  authReady: boolean
  folders: Folder[]
  notes: Note[]
  devices: Device[]
  todos: Todo[]
  loaded: boolean
  scope: Scope
  selectedId: string | null
  /** Every note highlighted in the list. Holds `selectedId` when non-empty. */
  selectedIds: string[]
  /** Where a Shift-selection measures from — the last plain click. */
  anchorId: string | null
  /**
   * The note the editor should take focus for. Selecting a note must NOT steal
   * focus, or the arrow keys move the caret instead of walking the list — which
   * is the opposite of how Notes behaves.
   */
  focusNoteId: string | null
  /** In-flight confirmation, rendered by <ConfirmDialog>. */
  confirmState: ConfirmRequest | null

  /** True while the key is held in memory for this session. */
  query: string
  sortKey: SortKey
  viewMode: ViewMode
  theme: Theme

  setUser: (u: User | null) => void
  setScope: (s: Scope) => void
  select: (id: string | null) => void
  setQuery: (q: string) => void
  requestEditorFocus: (id: string | null) => void
  /**
   * In-app replacement for window.confirm. The native dialog borrows the app
   * icon — Electron's own when unpackaged — and looks foreign on web and
   * Android, so confirmations are rendered in the app instead.
   */
  ask: (req: Omit<ConfirmRequest, 'resolve'>) => Promise<boolean>
  resolveConfirm: (answer: boolean) => void

  setSortKey: (k: SortKey) => void
  setViewMode: (v: ViewMode) => void
  setTheme: (t: Theme) => void

  /** Past states of the note whose history panel is open. */
  versions: Version[]
  /** Which note the history panel is showing, or null when it is closed. */
  historyFor: string | null
  openHistory: (noteId: string) => void
  closeHistory: () => void
  restoreVersion: (versionId: string) => void

  /** Recently deleted notes, newest last. Capped: this is an undo, not history. */
  undoStack: UndoEntry[]
  undo: () => void

  /** Forces a snapshot now, whatever the interval says. */
  backupNow: () => Promise<string | null>
  selectRange: (ids: string[], lead: string) => void
  toggleSelected: (id: string) => void
  /** Bulk forms of the single-note actions, used by a multi-selection. */
  trashMany: (ids: string[]) => void
  deleteForeverMany: (ids: string[]) => void
  setPinnedMany: (ids: string[], pinned: boolean) => void
  moveMany: (ids: string[], folderId: string | null) => void

  newNote: () => string | null
  saveNote: (id: string, content: Doc) => void
  flushPending: () => void
  togglePin: (id: string) => void
  trashNote: (id: string) => void
  restoreNote: (id: string) => void
  deleteForever: (id: string) => void
  emptyTrash: () => void
  moveNote: (id: string, folderId: string | null) => void
  duplicateNote: (id: string) => void
  addTagToNote: (id: string, tag: string) => void

  addTodo: (text: string) => void
  setTodoStatus: (id: string, status: TodoStatus) => void
  cycleTodoStatus: (id: string) => void
  updateTodoText: (id: string, text: string) => void
  deleteTodo: (id: string) => void
  clearCompletedTodos: () => void

  revokeDevice: (id: string) => void
  signOutEverywhere: () => Promise<void>

  addFolder: (name: string) => void
  renameFolder: (id: string, name: string) => void
  deleteFolder: (id: string) => void
  deleteFolders: (ids: string[]) => void
}

/**
 * Identifies this running client (this tab, this Electron window). Writes carry
 * it so a client can tell its own echo from a genuine remote edit.
 */
export const CLIENT_ID = Math.random().toString(36).slice(2) + Date.now().toString(36)

/**
 * Firestore caps nested map/array depth at 20 levels, and a ProseMirror tree
 * costs ~3 of those per document level — deeply nested lists blow the limit and
 * the write is rejected outright. Storing the document as a JSON string sidesteps
 * nesting entirely (and is smaller, with no mapValue overhead).
 *
 * Reads accept both shapes so notes written before this change keep working;
 * they convert to a string the next time they are saved.
 */
export function encodeContent(doc: Doc): string {
  return JSON.stringify(doc)
}

export function decodeContent(raw: unknown): Doc {
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as Doc } catch { return EMPTY_DOC }
  }
  return (raw as Doc) ?? EMPTY_DOC
}

export interface ConfirmRequest {
  title: string
  message?: string
  confirmLabel?: string
  danger?: boolean
  resolve: (answer: boolean) => void
}

/**
 * The tokens define light by default, dark under prefers-color-scheme, and both
 * again under an explicit data-theme. Setting (or clearing) the attribute on
 * <html> is all that is needed to pin or follow the system.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', theme)
}

let unsubs: Array<() => void> = []
let unsubHistory: (() => void) | null = null

const notesCol = (uid: string) => collection(db, 'users', uid, 'notes')
const foldersCol = (uid: string) => collection(db, 'users', uid, 'folders')
const devicesCol = (uid: string) => collection(db, 'users', uid, 'devices')
const todosCol = (uid: string) => collection(db, 'users', uid, 'todos')
const sessionDoc = (uid: string) => doc(db, 'users', uid, 'meta', 'session')
const versionsCol = (uid: string, noteId: string) =>
  collection(db, 'users', uid, 'notes', noteId, 'versions')

/** How many deletions can be walked back. An undo, not a history. */
const UNDO_DEPTH = 25

/** Forgets steps naming notes that no longer exist to be restored. */
function dropFromUndo(stack: UndoEntry[], goneIds: string[]): UndoEntry[] {
  const gone = new Set(goneIds)
  return stack
    .map((e) => ({ ...e, notes: e.notes.filter((n) => !gone.has(n.id)) }))
    .filter((e) => e.notes.length > 0)
}

/** When each note was last snapshotted, so a typing burst archives only once. */
const lastSnapshot = new Map<string, number>()

/**
 * Drops the per-session snapshot bookkeeping. Called when a session ends, so
 * the next one archives the state it starts from rather than assuming the
 * previous session's timing still applies.
 */
export function forgetSnapshotTimes(): void { lastSnapshot.clear() }

/** Deletes a note's whole version history; Firestore has no cascade. */
async function purgeVersions(uid: string, noteId: string): Promise<void> {
  const snap = await getDocs(versionsCol(uid, noteId))
  await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)))
}

/**
 * Keeps the state a note is about to leave behind. Called before the new
 * content is written, so what lands in history is genuinely a previous
 * revision and never a duplicate of what is on screen.
 */
async function archive(uid: string, note: Note, next: Doc): Promise<void> {
  if (sameDoc(note.content, next)) return
  if (!shouldSnapshot(lastSnapshot.get(note.id), Date.now())) return
  // Encrypted and still locked: the content in memory is a placeholder, and
  // archiving it would overwrite history with blanks.
  lastSnapshot.set(note.id, Date.now())

  const col = versionsCol(uid, note.id)
  await setDoc(doc(col), {
    content: encodeContent(note.content), title: note.title, savedAt: note.updatedAt,
  })

  const snap = await getDocs(col)
  const stale = prunable(snap.docs.map((d) => ({ id: d.id, savedAt: Number(d.data().savedAt) || 0 })))
  await Promise.all(stale.map((id) => deleteDoc(doc(col, id))))
}

/** Turns one Firestore document into a Note. */
export function noteFromDoc(id: string, data: Record<string, unknown>): Note {
  return { ...data, id, content: decodeContent(data.content) } as unknown as Note
}

let heartbeat: ReturnType<typeof setInterval> | null = null
let backupTimer: ReturnType<typeof setInterval> | null = null
/** When this session last wrote a backup, and of what. */
let lastBackupAt: number | null = null
let lastBackupPrint: string | null = null

/**
 * Snapshots the whole account to Cloud Storage when something has changed and
 * the interval has passed. Deliberately outside Firestore: a backup is only
 * worth having if it survives whatever goes wrong with the notes themselves.
 */
async function maybeBackup(uid: string, force = false): Promise<string | null> {
  const { notes, folders, todos, loaded } = useStore.getState()
  if (!loaded) return null
  const print = fingerprint(notes, folders, todos)
  if (!force && !backupDue(lastBackupAt, lastBackupPrint, print, Date.now())) return null
  const at = Date.now()
  // Recorded before the upload: a failure should not queue up a retry every
  // tick against a backend that is evidently unhappy.
  lastBackupAt = at
  lastBackupPrint = print
  try {
    return await writeBackup(uid, buildSnapshot(notes, folders, todos, at))
  } catch {
    return null
  }
}

export const useStore = create<State>((set, get) => ({
  user: null,
  authReady: false,
  folders: [],
  notes: [],
  devices: [],
  versions: [],
  historyFor: null,
  undoStack: [],
  todos: [],
  loaded: false,
  scope: { kind: 'all' },
  selectedId: null,
  selectedIds: [],
  anchorId: null,
  focusNoteId: null,
  confirmState: null,
  query: '',
  sortKey: (localStorage.getItem('sortKey') as SortKey) || 'edited',
  viewMode: (localStorage.getItem('viewMode') as ViewMode) || 'list',
  theme: (localStorage.getItem('theme') as Theme) || 'system',

  setUser: (user) => {
    unsubs.forEach((u) => u())
    unsubs = []
    unsubHistory?.()
    unsubHistory = null
    forgetSnapshotTimes()
    if (heartbeat) { clearInterval(heartbeat); heartbeat = null }
    if (backupTimer) { clearInterval(backupTimer); backupTimer = null }
    lastBackupAt = null
    lastBackupPrint = null
    set({
      user, authReady: true, notes: [], folders: [], devices: [], todos: [],
      loaded: false, selectedId: null, undoStack: [],
    })
    if (!user) return

    const uid = user.uid

    // Register this installation and keep its lastSeenAt fresh.
    const me = doc(devicesCol(uid), DEVICE_ID)
    const { label, platform } = describeDevice()
    void setDoc(me, { label, platform, lastSeenAt: Date.now(), revokedAt: null }, { merge: true })
    void setDoc(me, { createdAt: Date.now() }, { merge: true })
    heartbeat = setInterval(() => {
      void setDoc(me, { lastSeenAt: Date.now() }, { merge: true })
    }, 120_000)
    // Checked often, written rarely: the interval and the change check decide
    // whether anything is actually uploaded.
    backupTimer = setInterval(() => { void maybeBackup(uid) }, 60_000)
    unsubs.push(
      onSnapshot(query(foldersCol(uid), orderBy('order')), (snap) => {
        set({ folders: snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Folder) })
      }),
      onSnapshot(query(notesCol(uid), orderBy('updatedAt', 'desc')), (snap) => {
        const raw = snap.docs.map((d) => noteFromDoc(d.id, d.data()))
        set({ notes: raw, loaded: true })
      }),
      onSnapshot(query(todosCol(uid), orderBy('order')), (snap) => {
        set({ todos: snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Todo) })
      }),
      onSnapshot(devicesCol(uid), (snap) => {
        const devices = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Device)
        set({ devices })
        // Revoked from another device: sign this one out promptly.
        const self = devices.find((d) => d.id === DEVICE_ID)
        if (self?.revokedAt) void signOut()
      }),
    )
  },

  setScope: (scope) => set({ scope, selectedId: null, selectedIds: [], anchorId: null }),

  select: (selectedId) => {
    get().flushPending()
    set({
      selectedId, focusNoteId: null,
      selectedIds: selectedId ? [selectedId] : [],
      // A plain click is where a later Shift-click measures from.
      anchorId: selectedId,
    })
  },

  /** Shift-click and Shift+Arrow: `ids` is the run, in display order. */
  selectRange: (ids, lead) => {
    get().flushPending()
    set({ selectedIds: ids, selectedId: lead, focusNoteId: null })
  },

  /** Cmd/Ctrl-click: add or remove one note without disturbing the rest. */
  toggleSelected: (id) => {
    const { selectedIds, selectedId } = get()
    get().flushPending()
    const has = selectedIds.includes(id)
    const next = has ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]
    set({
      selectedIds: next,
      // Deselecting the open note hands the editor to whatever is left.
      selectedId: has ? (selectedId === id ? next.at(-1) ?? null : selectedId) : id,
      anchorId: id,
      focusNoteId: null,
    })
  },
  setQuery: (query) => set({ query }),
  requestEditorFocus: (focusNoteId) => set({ focusNoteId }),

  ask: (req) => new Promise<boolean>((resolve) => set({ confirmState: { ...req, resolve } })),
  resolveConfirm: (answer) => {
    const pending = get().confirmState
    set({ confirmState: null })
    pending?.resolve(answer)
  },

  setSortKey: (sortKey) => { try { localStorage.setItem('sortKey', sortKey) } catch { /* private mode */ } set({ sortKey }) },
  setViewMode: (viewMode) => { try { localStorage.setItem('viewMode', viewMode) } catch { /* private mode */ } set({ viewMode }) },
  setTheme: (theme) => {
    try { localStorage.setItem('theme', theme) } catch { /* private mode */ }
    applyTheme(theme)
    set({ theme })
  },

  openHistory: (noteId) => {
    const { user } = get()
    if (!user) return
    set({ historyFor: noteId, versions: [] })
    get().flushPending()
    unsubHistory?.()
    unsubHistory = onSnapshot(
      query(versionsCol(user.uid, noteId), orderBy('savedAt', 'desc')),
      (snap) => {
        // Bail if the panel moved on while this listener was in flight.
        if (get().historyFor !== noteId) return
        const versions = snap.docs.map((d) => {
          const data = d.data()
          return {
            id: d.id,
            savedAt: Number(data.savedAt) || 0,
            content: decodeContent(data.content),
            title: String(data.title ?? ''),
          }
        })
        set({ versions })
      },
    )
  },

  closeHistory: () => {
    unsubHistory?.()
    unsubHistory = null
    set({ historyFor: null, versions: [] })
  },

  restoreVersion: (versionId) => {
    const { historyFor, versions } = get()
    const version = versions.find((v) => v.id === versionId)
    if (!historyFor || !version) return
    // Force a snapshot of what is on screen now, so reverting is itself
    // undoable however recently the last one was taken.
    lastSnapshot.delete(historyFor)
    get().saveNote(historyFor, version.content)
    get().flushPending()
    set({ historyFor: null, versions: [] })
  },

  backupNow: async () => {
    const { user } = get()
    if (!user) return null
    return maybeBackup(user.uid, true)
  },

  newNote: () => {
    const { user, scope } = get()
    if (!user) return null
    const ref = doc(notesCol(user.uid))
    const now = Date.now()
    const note: Omit<Note, 'id'> = {
      folderId: scope.kind === 'folder' ? scope.id : null,
      title: 'New Note',
      snippet: '',
      text: '',
      tags: [],
      content: EMPTY_DOC,   // serialised below
      pinned: false,
      trashedAt: null,
      createdAt: now,
      updatedAt: now,
    }
    void setDoc(ref, { ...note, content: encodeContent(note.content) })
    set({ selectedId: ref.id, focusNoteId: ref.id })
    return ref.id
  },

  saveNote: (id, content) => {
    const { user } = get()
    if (!user) return
    pending.set(id, content)
    schedule(id, () => {
      const body = pending.get(id)
      pending.delete(id)
      if (!body) return
      const meta = deriveMeta(body)
      const note = get().notes.find((n) => n.id === id)
      const ref = doc(notesCol(user.uid), id)
      // Client clock, not serverTimestamp(): an offline edit must sort correctly
      // in the list immediately, before it can reach a server.
      const stamp = { updatedAt: Date.now(), clientId: CLIENT_ID }

      if (note) void archive(user.uid, note, body)

      void updateDoc(ref, { content: encodeContent(body), ...meta, ...stamp })
    })
  },

  flushPending: () => {
    for (const [id, timer] of timers) { clearTimeout(timer); timers.delete(id); runNow.get(id)?.() }
    runNow.clear()
    firstAt.clear()
  },

  togglePin: (id) => {
    const { user, notes } = get()
    const note = notes.find((n) => n.id === id)
    if (!user || !note) return
    void updateDoc(doc(notesCol(user.uid), id), { pinned: !note.pinned })
  },

  trashNote: (id) => {
    const { user, selectedId, notes, undoStack } = get()
    if (!user) return
    const note = notes.find((n) => n.id === id)
    void updateDoc(doc(notesCol(user.uid), id), { trashedAt: Date.now() })
    if (note) {
      set({
        undoStack: [
          ...undoStack,
          { kind: 'trash', notes: [{ id, updatedAt: note.updatedAt }] } satisfies UndoEntry,
        ].slice(-UNDO_DEPTH),
      })
    }
    if (selectedId === id) set({ selectedId: null })
  },

  /**
   * One batch, and one undo step: deleting eight notes should take one Cmd+Z
   * to bring back, not eight.
   */
  trashMany: (ids) => {
    const { user, notes, undoStack, selectedId } = get()
    if (!user || !ids.length) return
    const hit = notes.filter((n) => ids.includes(n.id))
    if (!hit.length) return
    const batch = writeBatch(db)
    const at = Date.now()
    hit.forEach((n) => batch.update(doc(notesCol(user.uid), n.id), { trashedAt: at }))
    void batch.commit()
    set({
      undoStack: [
        ...undoStack,
        { kind: 'trash', notes: hit.map((n) => ({ id: n.id, updatedAt: n.updatedAt })) } satisfies UndoEntry,
      ].slice(-UNDO_DEPTH),
      selectedIds: [],
      anchorId: null,
      selectedId: selectedId && ids.includes(selectedId) ? null : selectedId,
    })
  },

  deleteForeverMany: (ids) => {
    const { user } = get()
    if (!user || !ids.length) return
    ids.forEach((id) => get().deleteForever(id))
    set({ selectedIds: [], anchorId: null })
  },

  /**
   * Explicit `pinned` rather than a per-note toggle: a mixed selection should
   * end up all pinned or all unpinned, not inverted note by note.
   */
  setPinnedMany: (ids, pinned) => {
    const { user } = get()
    if (!user || !ids.length) return
    const batch = writeBatch(db)
    ids.forEach((id) => batch.update(doc(notesCol(user.uid), id), { pinned }))
    void batch.commit()
  },

  moveMany: (ids, folderId) => {
    const { user } = get()
    if (!user || !ids.length) return
    ids.forEach((id) => get().moveNote(id, folderId))
  },

  undo: () => {
    const { user, undoStack, notes } = get()
    const last = undoStack.at(-1)
    if (!user || !last) return
    set({ undoStack: undoStack.slice(0, -1) })

    for (const { id, updatedAt } of last.notes) {
      // Gone for good — emptying the trash prunes the stack, but another device
      // can delete it out from under us between the trash and the undo.
      if (!notes.some((n) => n.id === id)) continue
      // The original updatedAt goes back too: an undone delete should put the
      // note where it was, not shuffle it to the top of the list.
      void updateDoc(doc(notesCol(user.uid), id), { trashedAt: null, updatedAt })
    }
    const back = last.notes
      .map(({ id }) => notes.find((n) => n.id === id))
      .filter((n): n is Note => n != null)
      // As they will be once the write lands — that is what decides where they
      // belong, not the trashed state still in the local snapshot.
      .map((n) => ({ ...n, trashedAt: null }))
    if (!back.length) return

    const { scope } = get()
    // Staying put is the whole point: undoing a delete inside a folder should
    // leave you in that folder, with the list where you left it. Only move when
    // the note would come back somewhere you cannot see it — out of the trash,
    // or into a folder other than the one on screen.
    const lost = !back.some((n) => scopeShows(scope, n))
    const home: Scope = back[0].folderId
      ? { kind: 'folder', id: back[0].folderId }
      : { kind: 'all' }

    set({
      selectedId: back[0].id,
      selectedIds: back.map((n) => n.id),
      anchorId: back[0].id,
      ...(lost ? { scope: home } : {}),
    })
  },

  restoreNote: (id) => {
    const { user } = get()
    if (!user) return
    void updateDoc(doc(notesCol(user.uid), id), { trashedAt: null, updatedAt: Date.now() })
  },

  deleteForever: (id) => {
    const { user, selectedId } = get()
    if (!user) return
    // Firestore does not cascade: the versions would outlive the note and stay
    // readable, which for a locked note is the whole point of deleting it.
    void purgeVersions(user.uid, id).finally(() => {
      void deleteDoc(doc(notesCol(user.uid), id))
    })
    if (get().historyFor === id) get().closeHistory()
    set({ undoStack: dropFromUndo(get().undoStack, [id]) })
    if (selectedId === id) set({ selectedId: null })
  },

  emptyTrash: () => {
    const { user, notes } = get()
    if (!user) return
    const batch = writeBatch(db)
    const purged = notes.filter((n) => n.trashedAt)
    purged.forEach((n) => batch.delete(doc(notesCol(user.uid), n.id)))
    void batch.commit()
    set({ selectedId: null, undoStack: dropFromUndo(get().undoStack, purged.map((n) => n.id)) })
  },

  moveNote: (id, folderId) => {
    const { user, notes } = get()
    if (!user) return
    const note = notes.find((n) => n.id === id)
    void updateDoc(doc(notesCol(user.uid), id), { folderId })
    // Re-save so it is written encrypted or plain to match its new folder.
    if (note) setTimeout(() => { get().saveNote(id, note.content); get().flushPending() }, 0)
  },

  duplicateNote: (id) => {
    const { user, notes } = get()
    const src = notes.find((n) => n.id === id)
    if (!user || !src) return
    const ref = doc(notesCol(user.uid))
    const now = Date.now()
    const { id: _drop, ...rest } = src
    void _drop
    void setDoc(ref, {
      ...rest, content: encodeContent(src.content), title: `${src.title} copy`,
      createdAt: now, updatedAt: now, pinned: false, clientId: CLIENT_ID,
    })
    set({ selectedId: ref.id, focusNoteId: ref.id })
  },

  addTagToNote: (id, tag) => {
    const { user, notes } = get()
    const note = notes.find((n) => n.id === id)
    if (!user || !note) return
    if ((note.tags ?? []).includes(tag)) return
    // Tags are inline text in Apple Notes, so adding one appends it to the body.
    const content = {
      ...note.content,
      content: [...(note.content.content ?? []), { type: 'paragraph', content: [{ type: 'text', text: `#${tag}` }] }],
    } as Doc
    const { title, snippet, text, tags } = deriveMeta(content)
    void updateDoc(doc(notesCol(user.uid), id), {
      content: encodeContent(content), title, snippet, text, tags,
      updatedAt: Date.now(), clientId: CLIENT_ID,
    })
  },

  addTodo: (text) => {
    const { user, todos } = get()
    if (!user || !text.trim()) return
    const now = Date.now()
    // New items land at the bottom of the active list.
    const maxOrder = todos.length ? Math.max(...todos.map((t) => t.order)) : 0
    void setDoc(doc(todosCol(user.uid)), {
      text: text.trim(), status: 'open', order: maxOrder + 1,
      createdAt: now, updatedAt: now, completedAt: null, clientId: CLIENT_ID,
    })
  },

  setTodoStatus: (id, status) => {
    const { user } = get()
    if (!user) return
    void updateDoc(doc(todosCol(user.uid), id), {
      status,
      completedAt: status === 'done' ? Date.now() : null,
      updatedAt: Date.now(),
      clientId: CLIENT_ID,
    })
  },

  cycleTodoStatus: (id) => {
    const { todos } = get()
    const todo = todos.find((t) => t.id === id)
    if (!todo) return
    const next: TodoStatus =
      todo.status === 'open' ? 'in_progress' : todo.status === 'in_progress' ? 'done' : 'open'
    get().setTodoStatus(id, next)
  },

  updateTodoText: (id, text) => {
    const { user } = get()
    if (!user) return
    if (!text.trim()) { get().deleteTodo(id); return }
    void updateDoc(doc(todosCol(user.uid), id), {
      text: text.trim(), updatedAt: Date.now(), clientId: CLIENT_ID,
    })
  },

  deleteTodo: (id) => {
    const { user } = get()
    if (!user) return
    void deleteDoc(doc(todosCol(user.uid), id))
  },

  clearCompletedTodos: () => {
    const { user, todos } = get()
    if (!user) return
    const done = todos.filter((t) => t.status === 'done')
    if (!done.length) return
    const batch = writeBatch(db)
    done.forEach((t) => batch.delete(doc(todosCol(user.uid), t.id)))
    void batch.commit()
  },

  revokeDevice: (id) => {
    const { user } = get()
    if (!user) return
    // Cooperative: the target signs itself out when it sees this. Hard
    // revocation of the refresh token needs the Admin SDK — see README.
    void setDoc(doc(devicesCol(user.uid), id), { revokedAt: Date.now() }, { merge: true })
  },

  signOutEverywhere: async () => {
    const { user } = get()
    if (!user) return
    // Enforced server-side: the rules compare each request's auth_time against
    // this floor, so every existing session — including this one — is refused
    // until it signs in again.
    await setDoc(sessionDoc(user.uid), { validAfterSeconds: Math.floor(Date.now() / 1000) })
    await signOut()
  },

  addFolder: (name) => {
    const { user, folders } = get()
    if (!user || !name.trim()) return
    const ref = doc(foldersCol(user.uid))
    void setDoc(ref, { name: name.trim(), order: folders.length, createdAt: Date.now() })
  },

  renameFolder: (id, name) => {
    const { user } = get()
    if (!user || !name.trim()) return
    void updateDoc(doc(foldersCol(user.uid), id), { name: name.trim() })
  },

  deleteFolder: (id) => { get().deleteFolders([id]) },

  /** One batch for the whole selection, so the notes all move together. */
  deleteFolders: (ids) => {
    const { user, notes, scope } = get()
    if (!user || !ids.length) return
    const gone = new Set(ids)
    // Apple Notes sends a deleted folder's notes to Recently Deleted.
    const batch = writeBatch(db)
    const at = Date.now()
    notes.filter((n) => n.folderId != null && gone.has(n.folderId) && !n.trashedAt)
      .forEach((n) => batch.update(doc(notesCol(user.uid), n.id), { trashedAt: at, folderId: null }))
    ids.forEach((id) => batch.delete(doc(foldersCol(user.uid), id)))
    void batch.commit()
    if (scope.kind === 'folder' && gone.has(scope.id)) {
      set({ scope: { kind: 'all' }, selectedId: null, selectedIds: [], anchorId: null })
    }
  },
}))

// ---- debounced autosave -------------------------------------------------
/** Quiet period after the last keystroke before a save goes out. */
const SAVE_DELAY = 250
/**
 * Ceiling on how long an edit can sit unsent. Without this, a plain debounce
 * resets on every keystroke, so continuous typing never syncs at all until you
 * stop — which reads as "sync is slow" on the other device.
 */
const MAX_WAIT = 1000

const pending = new Map<string, Doc>()
const timers = new Map<string, ReturnType<typeof setTimeout>>()
const runNow = new Map<string, () => void>()
const firstAt = new Map<string, number>()

function schedule(id: string, run: () => void) {
  const now = Date.now()
  if (!firstAt.has(id)) firstAt.set(id, now)
  clearTimeout(timers.get(id))
  runNow.set(id, run)
  const waited = now - (firstAt.get(id) ?? now)
  const delay = Math.min(SAVE_DELAY, Math.max(0, MAX_WAIT - waited))
  timers.set(id, setTimeout(() => {
    timers.delete(id); runNow.delete(id); firstAt.delete(id); run()
  }, delay))
}

/** True while this client has an edit to `id` that hasn't been written yet. */
export function hasPendingEdit(id: string): boolean {
  return pending.has(id)
}

// ---- selectors ----------------------------------------------------------
/** Whether this note would appear in the list under `scope`, ignoring search. */
export function scopeShows(scope: Scope, note: Note): boolean {
  if (scope.kind === 'todo') return false
  if (scope.kind === 'trash') return note.trashedAt != null
  if (note.trashedAt != null) return false
  if (scope.kind === 'folder') return note.folderId === scope.id
  if (scope.kind === 'tag') return (note.tags ?? []).includes(scope.tag)
  return true
}

export function visibleNotes(s: State): Note[] {
  const inScope = s.notes.filter((n) => scopeShows(s.scope, n))

  const by = (a: Note, b: Note) => {
    switch (s.sortKey) {
      case 'created': return b.createdAt - a.createdAt
      case 'title': return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })
      default: return b.updatedAt - a.updatedAt
    }
  }
  return inScope
    .filter((n) => matchesQuery(n, s.query))
    .sort((a, b) => Number(b.pinned) - Number(a.pinned) || by(a, b))
}

/** Active items (open + in-progress) in display order. */
export function activeTodos(s: State): Todo[] {
  return s.todos
    .filter((t) => t.status !== 'done')
    // In-progress floats above not-started, then by manual order.
    .sort((a, b) =>
      Number(b.status === 'in_progress') - Number(a.status === 'in_progress') || a.order - b.order)
}

/** Completed items, most recently finished first. */
export function completedTodos(s: State): Todo[] {
  return s.todos
    .filter((t) => t.status === 'done')
    .sort((a, b) => (b.completedAt ?? b.updatedAt) - (a.completedAt ?? a.updatedAt))
}

/** Every #tag in use, with counts, for the sidebar. */
export function allTags(s: State): Array<{ tag: string; count: number }> {
  const counts = new Map<string, number>()
  for (const n of s.notes) {
    if (n.trashedAt != null) continue
    for (const t of n.tags ?? []) counts.set(t, (counts.get(t) ?? 0) + 1)
  }
  return [...counts.entries()].map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => a.tag.localeCompare(b.tag))
}

export function folderCount(s: State, id: string | null): number {
  if (id === null) return s.notes.filter((n) => !n.trashedAt).length
  return s.notes.filter((n) => n.folderId === id && !n.trashedAt).length
}

export function trashCount(s: State): number {
  return s.notes.filter((n) => n.trashedAt != null).length
}

export { DAYS_IN_TRASH }
